import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Check } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/auth'
import { formatTimestamp } from '../../lib/utils'

const TYPE_ICONS = {
  application_received: '📋',
  animal_status_change: '🐾',
  animal_placed: '🎉',
  chat_mention: '💬',
  low_inventory: '📦',
  document_uploaded: '📄',
  meeting_scheduled: '📅',
  agreement_signed: '✅',
  vet_reminder: '💉',
  foster_assignment: '🏠',
  board_card_assigned: '📌',
  inbox_email: '📧',
}

export default function NotificationBell() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const dropdownRef = useRef(null)

  useEffect(() => {
    if (!user) return
    loadNotifications()

    // Poll every 30s instead of Realtime subscription.
    // The Supabase Realtime channel API has a race condition where
    // .on() after .subscribe() throws in React's render cycle —
    // even with unique channel names. Polling is reliable and the
    // bell doesn't need sub-second updates.
    const interval = setInterval(loadNotifications, 30000)
    return () => clearInterval(interval)
  }, [user])

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  async function loadNotifications() {
    const { data, count } = await supabase
      .from('notifications')
      .select('*', { count: 'exact' })
      .eq('user_id', user.id)
      .eq('is_read', false)
      .order('created_at', { ascending: false })
      .limit(20)
    setNotifications(data || [])
    setUnreadCount(count || 0)
  }

  async function markRead(n) {
    await supabase.from('notifications').update({ is_read: true }).eq('id', n.id)
    setOpen(false)
    if (n.link) navigate(n.link)
    loadNotifications()
  }

  async function markAllRead() {
    await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', user.id)
      .eq('is_read', false)
    loadNotifications()
  }

  if (!user) return null

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(v => !v)}
        className="relative w-9 h-9 rounded-full flex items-center justify-center hover:bg-surface-container-high transition-colors"
        aria-label="Notifications"
      >
        <Bell size={18} strokeWidth={1.5} className="text-on-surface-variant" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-error text-on-error text-[0.625rem] font-bold flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 max-h-[70vh] bg-surface-container-lowest rounded-md shadow-float overflow-hidden z-50">
          <div className="flex items-center justify-between px-4 py-3">
            <h3 className="text-sm font-semibold tracking-[0.05em] text-on-surface">Notifications</h3>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs text-primary font-semibold">
                <Check size={12} className="inline mr-1" /> Mark All Read
              </button>
            )}
          </div>
          <div className="overflow-y-auto max-h-[55vh]">
            {notifications.length === 0 ? (
              <p className="text-xs text-on-surface-variant text-center py-8">No new notifications</p>
            ) : (
              notifications.map(n => (
                <button
                  key={n.id}
                  onClick={() => markRead(n)}
                  className="w-full text-left px-4 py-3 hover:bg-surface-container-low transition-colors flex items-start gap-3"
                >
                  <span className="text-base flex-shrink-0 mt-0.5">{TYPE_ICONS[n.type] || '🔔'}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-on-surface">{n.title}</p>
                    {n.body && <p className="text-xs text-on-surface-variant line-clamp-2">{n.body}</p>}
                    <span className="text-[0.625rem] font-mono text-on-surface-variant">{formatTimestamp(n.created_at)}</span>
                  </div>
                  {!n.is_read && <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-2" />}
                </button>
              ))
            )}
          </div>
          <div className="px-4 py-2" style={{ borderTop: '1px solid rgba(200,195,184,0.2)' }}>
            <button
              onClick={() => { setOpen(false); navigate('/notifications') }}
              className="text-xs text-primary font-semibold w-full text-center"
            >
              View All Notifications
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
